import { apiFetch } from "@/lib/api";

import type { QualityNcr } from "./qualityEventNcrApi";
import type { QualityRmaDetailResponse } from "./rmaApi";

export type QualityRcaStatus = "open" | "closed";

export interface QualityRcaListRow {
  id: number;
  createdAt: string;
  status: QualityRcaStatus;
  problemStatement: string | null;
  summary: string | null;
  rmaId: number | null;
  rmaNumber: string | null;
  qualityCaseId: number | null;
  qualityEventId: number | null;
  openedByDisplay: string | null;
}

export interface QualityRcaHeader {
  id: number;
  status: QualityRcaStatus;
  openedAt: string;
  openedBy: string;
  openedByDisplay: string | null;
  openedByAvatarUrl: string | null;
  openedAutomatically: boolean;
  summary: string | null;
  problemStatement: string | null;
  rmaId: number | null;
  qualityCaseId: number | null;
  qualityEventId: number | null;
  form: Record<string, unknown> | null;
}

export interface NcrDeviceWork {
  qualityIncidentId: number;
  work: Record<string, unknown>;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface QualityRcaDetail {
  rca: QualityRcaHeader;
  rmas: QualityRmaDetailResponse[];
  ncrs: QualityNcr[];
  ncrDeviceWork: Record<number, NcrDeviceWork>;
  investigations: Array<{
    investigationId: number;
    qualityCaseId: number;
    investigationStatus: string;
    caseTitle: string;
  }>;
  qualityCase: { id: number; title: string; status: string } | null;
  qualityEvent: { id: number; summary: string | null; status: string | null } | null;
  qualityIncidentIds: number[];
}

export function qualityRcaQueryKey(rcaId: number): readonly unknown[] {
  return ["quality", "rca", rcaId];
}

export async function fetchQualityRcas(input: {
  search?: string;
  status?: QualityRcaStatus;
  limit?: number;
}): Promise<{ rows: QualityRcaListRow[] }> {
  return apiFetch<{ rows: QualityRcaListRow[] }>("/api/quality/rcas", {
    query: {
      search: input.search,
      status: input.status,
      limit: input.limit != null ? String(input.limit) : undefined,
    },
  });
}

export async function fetchQualityRca(id: number): Promise<QualityRcaDetail> {
  return apiFetch<QualityRcaDetail>(`/api/quality/rcas/${id}`);
}

export async function fetchQualityCaseRcas(caseId: number): Promise<{ rows: QualityRcaListRow[] }> {
  return apiFetch<{ rows: QualityRcaListRow[] }>(`/api/quality/cases/${caseId}/rcas`);
}

export interface QualityRcaLinkInput {
  problemStatement?: string | null;
  qualityCaseId?: number | null;
  qualityEventId?: number | null;
  rmaId?: number | null;
  qualityIncidentIds?: number[];
}

export async function createQualityRca(input: QualityRcaLinkInput): Promise<QualityRcaDetail> {
  return apiFetch<QualityRcaDetail>("/api/quality/rcas", { method: "POST", body: input });
}

export async function linkQualityRca(rcaId: number, input: QualityRcaLinkInput): Promise<QualityRcaDetail> {
  return apiFetch<QualityRcaDetail>(`/api/quality/rcas/${rcaId}/links`, { method: "POST", body: input });
}

export async function saveQualityRca(
  rcaId: number,
  patch: { form?: object; problemStatement?: string | null; status?: QualityRcaStatus },
): Promise<QualityRcaDetail> {
  return apiFetch<QualityRcaDetail>(`/api/quality/rcas/${rcaId}`, { method: "PATCH", body: patch });
}

export async function fetchQualityRcaCustomerPdf(rcaId: number): Promise<{ filename: string; pdfBase64: string }> {
  return apiFetch<{ filename: string; pdfBase64: string }>(`/api/quality/rcas/${rcaId}/customer-pdf`);
}
